// Components/Billing/SubscriptionStatus.jsx
import React, { useState, useEffect } from 'react';
import { Clock, AlertCircle, CheckCircle, CreditCard, Calendar } from 'lucide-react';
import { getBuildingSubscription } from '../../Fetch/fetchSubscription';
import styles from './SubscriptionStatus.module.css';

const SubscriptionStatus = ({ buildingId, onPaymentNeeded }) => {
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchSubscriptionStatus();
    }, [buildingId]);

    const fetchSubscriptionStatus = async () => {
        try {
            setLoading(true);
            const response = await getBuildingSubscription(buildingId);
            if (response.success) {
                setSubscription(response.data);
            } else {
                setError('שגיאה בטעינת נתוני המנוי');
            }
        } catch (error) {
            setError('שגיאה בטעינת נתוני המנוי');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Trial': return '#2196F3';
            case 'Active': return '#4CAF50';
            case 'Overdue': return '#FF9800';
            case 'Suspended': return '#F44336';
            default: return '#757575';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'Trial': return 'תקופת ניסיון';
            case 'Active': return 'פעיל';
            case 'Overdue': return 'חריגה מתשלום';
            case 'Suspended': return 'מושעה';
            default: return 'לא ידוע';
        }
    };

    const getStatusIcon = (subscription) => {
        if (subscription.isLocked) {
            return <AlertCircle className={styles.statusIcon} style={{ color: '#F44336' }} />;
        }
        if (subscription.status === 'Active') {
            return <CheckCircle className={styles.statusIcon} style={{ color: '#4CAF50' }} />;
        }
        if (subscription.status === 'Trial') {
            return <Clock className={styles.statusIcon} style={{ color: '#2196F3' }} />;
        }
        return <AlertCircle className={styles.statusIcon} style={{ color: '#FF9800' }} />;
    };

    if (loading) {
        return (
            <div className={styles.subscriptionCard}>
                <div className={styles.loading}>טוען נתוני מנוי...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.subscriptionCard}>
                <div className={styles.error}>{error}</div>
            </div>
        );
    }

    if (!subscription) {
        return null;
    }

    return (
        <div className={styles.subscriptionCard} dir="rtl">
            <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>
                    {getStatusIcon(subscription)}
                    סטטוס מנוי
                </h3>
                <div 
                    className={styles.statusBadge}
                    style={{ backgroundColor: getStatusColor(subscription.status) }}
                >
                    {getStatusText(subscription.status)}
                </div>
            </div>

            <div className={styles.cardContent}>
                {subscription.status === 'Trial' && (
                    <div className={styles.trialInfo}>
                        <div className={styles.trialDays}>
                            <Calendar className={styles.icon} />
                            <span className={styles.daysCount}>
                                {subscription.daysRemaining} ימים נותרו
                            </span>
                            <span className={styles.trialLabel}>בתקופת הניסיון</span>
                        </div>
                        <div className={styles.trialEnd}>
                            תאריך סיום: {new Date(subscription.trialEndDate).toLocaleDateString('he-IL')}
                        </div>
                        {subscription.daysRemaining <= 7 && (
                            <div className={styles.warningMessage}>
                                <AlertCircle size={16} />
                                תקופת הניסיון מסתיימת בקרוב. אנא עדכן פרטי תשלום.
                            </div>
                        )}
                    </div>
                )}

                {subscription.status === 'Active' && (
                    <div className={styles.activeInfo}>
                        <div className={styles.paymentInfo}>
                            <div className={styles.infoRow}>
                                <span>תשלום אחרון:</span>
                                <span>{new Date(subscription.lastPaymentDate).toLocaleDateString('he-IL')}</span>
                            </div>
                            <div className={styles.infoRow}>
                                <span>תשלום הבא:</span>
                                <span>{new Date(subscription.nextPaymentDue).toLocaleDateString('he-IL')}</span>
                            </div>
                            <div className={styles.infoRow}>
                                <span>מחיר חודשי:</span>
                                <span>₪{subscription.monthlyPrice}</span>
                            </div>
                        </div>
                    </div>
                )}

                {subscription.isLocked && (
                    <div className={styles.lockedInfo}>
                        <div className={styles.lockMessage}>
                            <AlertCircle className={styles.lockIcon} />
                            <div>
                                <div className={styles.lockTitle}>הגישה לבניין נחסמה</div>
                                <div className={styles.lockReason}>
                                    {subscription.status === 'Trial' 
                                        ? 'תקופת הניסיון הסתיימה' 
                                        : 'התשלום עבר את מועד הפירעון'}
                                </div>
                            </div>
                        </div>
                        <button 
                            className={styles.payButton}
                            onClick={() => onPaymentNeeded && onPaymentNeeded(subscription)}
                        >
                            <CreditCard size={16} />
                            עדכן תשלום לביטול החסימה
                        </button>
                    </div>
                )}

                {!subscription.isLocked && subscription.daysRemaining <= 7 && subscription.status === 'Trial' && (
                    <div className={styles.paymentActions}>
                        <button 
                            className={styles.upgradeButton}
                            onClick={() => onPaymentNeeded && onPaymentNeeded(subscription)}
                        >
                            <CreditCard size={16} />
                            שדרג למנוי מלא
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriptionStatus;